import * as z from "zod";

import type { Contract, Target } from "../types";

// Pinned wire format; this package stays free of DB codegen. //redontIgnore
export const listingRestrictionTiers = [
  "blocked",
  "action_needed",
  "compliance_info",
  "lane_notice",
] as const;

export const listingRestrictionCategories = [
  "cost",
  "dangerous_goods",
  "documentation_required",
  "information",
  "permit_required",
  "product_compliance",
  "prohibited",
  "quota",
] as const;

// Pinned wire format; this package stays free of DB codegen. //redontIgnore
export const landedCostItems = ["duties", "taxes", "fees"] as const;

const restrictionMatchSchema = z.object({
  tier: z.enum(listingRestrictionTiers),
  category: z.enum(listingRestrictionCategories).nullable(),
  matchHsCode: z.string(),
  confidenceScore: z.number().nullable(),
  summary: z.string(),
  sourceUrl: z.string().nullable(),
});

const restrictionSchema = z.object({
  // Pinned wire format; this package stays free of DB codegen. //redontIgnore
  state: z.enum(["allowed", "restricted", "unknown"]),
  screenedAt: z.string().nullable(),
  matches: z.array(restrictionMatchSchema),
});

const countrySchema = z.object({
  code: z.string(),
  marketName: z.string().nullable(),
  shopCurrency: z.string(),
  priceListCurrency: z.string(),
});

const productSchema = z.object({
  productId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  vendor: z.string().nullable(),
  tags: z.array(z.string()),
  hsCode: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
});

const variantSchema = z.object({
  variantId: z.string(),
  sku: z.string().nullable(),
  title: z.string().nullable(),
  priceMinor: z.number().int().nullable(),
  calculatedPriceMinor: z.number().int().nullable(),
  calculatedCompareAtMinor: z.number().int().nullable(),
  weightGrams: z.number().nullable(),
  hsCode: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
  restriction: restrictionSchema,
});

const landedCostSchema = z
  .object({
    duties: z.number(),
    taxes: z.number(),
    fees: z.number(),
    currency: z.string(),
  })
  .nullable();

const marketPolicySchema = z.object({
  marketId: z.string(),
  name: z.string().nullable(),
  markup: z.number(),
  // Pinned wire format; this package stays free of DB codegen. //redontIgnore
  markupStrategy: z.enum(["absorbing", "additive"]),
  includeInPrice: z.array(z.enum(landedCostItems)),
  excludeInCheckout: z.array(z.enum(landedCostItems)),
  coverWithMarkup: z.array(z.enum(landedCostItems)),
  hideRestricted: z.boolean(),
});

export const marketListingInputSchema = z.object({
  country: countrySchema,
  product: productSchema,
  variant: variantSchema,
  landedCost: landedCostSchema,
  marketPolicy: marketPolicySchema,
});
export type MarketListingInput = z.infer<typeof marketListingInputSchema>;

const listingDecisionSchema = z.object({
  publication: z.boolean().optional(),
  price: z.number().int().nullable().optional(),
  compareAtPrice: z.number().int().nullable().optional(),
  reason: z.string().optional(),
});

export const marketListingOutputSchema = z.union([
  listingDecisionSchema,
  z.boolean(),
  z.null(),
]);
export type MarketListingOutput = z.infer<typeof marketListingOutputSchema>;

export const marketListingTarget = {
  id: "cross-border.market.listing",
  displayName: "Market listing",
  description:
    "Decides how a variant is listed into a destination country: whether it " +
    "may be sold there, and at what price. Runs once per variant per country, " +
    "and receives the compliance screening verdict, the landed cost quoted for " +
    "the lane, the market's pricing policy, and the price Redo calculated. " +
    "Returning nothing leaves Redo's decision in place.",
} as const satisfies Target;

export const marketListingStarterCode = `\
export default (input: Input): Output => {
  if (input.product.tags.includes("hazmat") && input.country.code === "AU") {
    return { publication: false, reason: "hazmat: no AU air transport" };
  }

  if (input.variant.calculatedPriceMinor === null) {
    return {};
  }
  const rounded = Math.floor(input.variant.calculatedPriceMinor / 100) * 100 - 1;
  return { price: Math.max(rounded, 0) };
};
`;

export const marketListingContractV1: Contract = {
  id: "cross-border.market.listing@2026-07.1",
  targetId: marketListingTarget.id,
  inputSchema: marketListingInputSchema,
  outputSchema: marketListingOutputSchema,
  tools: [],
  sampleInput: {
    country: {
      code: "AU",
      marketName: "APAC",
      shopCurrency: "USD",
      priceListCurrency: "AUD",
    },
    product: {
      productId: "prod_1",
      title: "Trail Headlamp 400",
      description: "Rechargeable lithium-ion headlamp, 400 lumens.",
      vendor: "Summit Gear",
      tags: ["hazmat", "outdoor"],
      hsCode: "851310",
      countryOfOrigin: "CN",
    },
    variant: {
      variantId: "var_1",
      sku: "HL-400-BLK",
      title: "Black",
      priceMinor: 8999,
      calculatedPriceMinor: 14_999,
      calculatedCompareAtMinor: null,
      weightGrams: 180,
      hsCode: "851310",
      countryOfOrigin: "CN",
      restriction: {
        state: "restricted",
        screenedAt: "2026-07-20T00:00:00.000Z",
        matches: [
          {
            tier: "blocked",
            category: "product_compliance",
            matchHsCode: "851310",
            confidenceScore: 0.94,
            summary: "Lithium batteries prohibited on inbound air freight.",
            sourceUrl: "https://example.gov/au/lithium",
          },
        ],
      },
    },
    landedCost: { duties: 1200, taxes: 1500, fees: 0, currency: "AUD" },
    marketPolicy: {
      marketId: "mkt_01J8ZQ",
      name: "APAC",
      markup: 0.1,
      markupStrategy: "absorbing",
      includeInPrice: ["duties", "taxes"],
      excludeInCheckout: [],
      coverWithMarkup: [],
      hideRestricted: true,
    },
  } satisfies z.infer<typeof marketListingInputSchema>,
};
